import { Autocomplete, Stack, TextField } from '@mui/material';
import { useId } from 'react';

import { FormField, api, isDefined } from 'common';

import { CloseModeFormFields } from './CloseModeFormFields';
import { CreateModeFormFields } from './CreateModeFormFields';
import { LegacyModeFormFields } from './LegacyModeFormFields';
import { ReopenModeFormFields } from './ReopenModeFormFields';
import { ReplyModeFormFields } from './ReplyModeFormFields';
import { UpdateModeFormFields } from './UpdateModeFormFields';
import { useSelectedNodeField } from '../../ConfigFields/useSelectedNodeField';

const { ConversationBlockMode } = api.FlowDefinition;

enum ConversationUiMode {
  Legacy = 'Legacy',
  Close = 'Close',
  Reply = 'Reply',
  Create = 'Create',
  Reopen = 'Reopen',
  Update = 'Update',
}

interface UIModeOption {
  value: ConversationUiMode;
  label: string;
}

const UI_MODE_OPTIONS: UIModeOption[] = [
  { value: ConversationUiMode.Close, label: 'Close' },
  { value: ConversationUiMode.Reply, label: 'Reply' },
  { value: ConversationUiMode.Create, label: 'Create' },
  { value: ConversationUiMode.Reopen, label: 'Reopen' },
  { value: ConversationUiMode.Update, label: 'Update' },
];

const LEGACY_UI_MODE_OPTION: UIModeOption = { value: ConversationUiMode.Legacy, label: 'Legacy' };

const UI_MODE_OPTIONS_WITH_LEGACY: UIModeOption[] = [LEGACY_UI_MODE_OPTION, ...UI_MODE_OPTIONS];

const MODE_TO_UI_MODE_MAP = {
  [ConversationBlockMode.WaitUntilClosed]: ConversationUiMode.Legacy,
  [ConversationBlockMode.NonBlocking]: ConversationUiMode.Legacy,
  [ConversationBlockMode.Close]: ConversationUiMode.Close,
  [ConversationBlockMode.Reply]: ConversationUiMode.Reply,
  [ConversationBlockMode.Create]: ConversationUiMode.Create,
  [ConversationBlockMode.Reopen]: ConversationUiMode.Reopen,
  [ConversationBlockMode.Update]: ConversationUiMode.Update,
};

export function ConversationBlockFormFields() {
  const { value: existingConversationId, setValue: setExistingConversationId } =
    useSelectedNodeField<string>('config.existingConversationId');
  const { value: mode, setValue: setMode } =
    useSelectedNodeField<api.FlowDefinition.ConversationBlockMode>('config.mode');
  const { setValue: setPatientReplyHandler, value: patientReplyHandler } = useSelectedNodeField<
    | {
        versionTarget: api.FlowDefinition.FlowVersionTarget | undefined;
        flowDefinitionId: string | undefined;
        flowId: string | undefined;
        metadata: api.FlowDefinition.FlowArgument[] | undefined;
      }
    | undefined
  >('config.patientReplyHandler');

  const patientReplyHandlerEnabled = isDefined(patientReplyHandler);

  const uiMode = isDefined(mode) ? MODE_TO_UI_MODE_MAP[mode] : ConversationUiMode.Create;

  // Legacy mode is deprecated and should only be selectable when the block is already configured
  // to use it, so newly created blocks never surface it as an option.
  const isLegacyConfigured = uiMode === ConversationUiMode.Legacy;
  const uiModeOptions = isLegacyConfigured ? UI_MODE_OPTIONS_WITH_LEGACY : UI_MODE_OPTIONS;

  const handlePatientReplyHandlerChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const checked = event.target.checked;
    if (!checked) {
      setPatientReplyHandler(undefined);
    } else {
      setPatientReplyHandler({
        versionTarget: api.FlowDefinition.FlowVersionTarget.SpecificVersion,
        flowDefinitionId: undefined,
        flowId: undefined,
        metadata: undefined,
      });
    }
  };

  const uiModeFieldId = useId();

  return (
    <Stack gap={2}>
      <FormField id={uiModeFieldId} label="Mode">
        <Autocomplete
          options={uiModeOptions}
          value={uiModeOptions.find((option) => option.value === uiMode) || null}
          onChange={(_, newValue) => {
            if (!isDefined(newValue?.value) || newValue?.value === ConversationUiMode.Legacy) {
              setMode(ConversationBlockMode.WaitUntilClosed);
              setExistingConversationId('');
              return;
            }
            if (newValue?.value === ConversationUiMode.Close) {
              setMode(ConversationBlockMode.Close);
            }
            if (newValue?.value === ConversationUiMode.Reply) {
              setMode(ConversationBlockMode.Reply);
            }
            if (newValue?.value === ConversationUiMode.Create) {
              setMode(ConversationBlockMode.Create);
            }
            if (newValue?.value === ConversationUiMode.Reopen) {
              setMode(ConversationBlockMode.Reopen);
            }
            if (newValue?.value === ConversationUiMode.Update) {
              setMode(ConversationBlockMode.Update);
            }
          }}
          getOptionLabel={(option) => {
            if (typeof option === 'string') {
              return option;
            }
            return option.label;
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              helperText="Choose between Create, Reopen, Reply, Close, or Update modes"
              size="small"
            />
          )}
        />
      </FormField>

      {uiMode === ConversationUiMode.Legacy && (
        <LegacyModeFormFields
          existingConversationId={existingConversationId}
          patientReplyHandlerEnabled={patientReplyHandlerEnabled}
          onPatientReplyHandlerChange={handlePatientReplyHandlerChange}
        />
      )}
      {uiMode === ConversationUiMode.Close && <CloseModeFormFields />}
      {uiMode === ConversationUiMode.Reply && <ReplyModeFormFields />}
      {uiMode === ConversationUiMode.Create && <CreateModeFormFields />}
      {uiMode === ConversationUiMode.Reopen && <ReopenModeFormFields />}
      {uiMode === ConversationUiMode.Update && <UpdateModeFormFields />}
    </Stack>
  );
}
